import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  DualPaneSelectionDialog,
  type SelectionItem,
} from '@/components/shared/DualPaneSelectionDialog';
import { useEntities, useAllEntities } from '@/hooks';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import { useNotifications } from '@/stores/useNotificationStore';
import {
  personSizingService,
  type PersonSizingAssignmentInput,
} from '@/services/iem/PersonSizingService';
import type { Entity } from '@/types/Entity';
import { logger } from '@equip/utils/logger';
import { getErrorMessage } from '@/utils/httpClient';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { useMyPersonEntityId } from '@/hooks/useMyPersonEntityId';
import { useAuth } from '@/hooks/useAuth';
import { buildParentIdSet, isCatalogVariant } from '@/utils/catalogUtils';
import { parsePersonSizingImportFile } from './personSizingImport';
import { formatNomenclatureWithSize } from '@equip/utils';

const log = logger.child({ component: 'PersonSizingDialog' });

// --- Types ---

interface PersonSizingDialogProps {
  open: boolean;
  onClose: () => void;
  person?: Entity;
}

interface SizingDraft {
  // Key is the child catalog ID (the specific size)
  catalogId: string;
  // We also need to know the parent ID to group/display correctly
  parentId: string;
}

interface CatalogEntityData {
  parent_id?: string;
  nsn?: string;
  size?: string;
}

interface SizingEntityData {
  catalog_id?: string;
  parent_id?: string;
  person_id?: string;
}

type FilterModeValue = 'all' | 'paired' | 'unpaired';

// --- Helper Components ---

interface SizeSelectionDialogProps {
  open: boolean;
  parentItem: Entity | null;
  childItems: Entity[];
  onSelect: (child: Entity) => void;
  onClose: () => void;
}

const SizeSelectionDialog: React.FC<SizeSelectionDialogProps> = ({
  open,
  parentItem,
  childItems,
  onSelect,
  onClose,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Select Size</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" gutterBottom>
          {parentItem?.display_name}
        </Typography>
        <List>
          {childItems.length === 0 ? (
            <ListItem>
              <ListItemText primary="No sizes available" />
            </ListItem>
          ) : (
            childItems.map((child) => (
              <ListItemButton key={child.id} onClick={() => onSelect(child)}>
                <ListItemText
                  primary={formatNomenclatureWithSize(
                    child.display_name,
                    (child.entity_data as CatalogEntityData)?.size,
                  )}
                  secondary={(child.entity_data as CatalogEntityData)?.nsn}
                />
              </ListItemButton>
            ))
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Main Component ---

export const PersonSizingDialog: React.FC<PersonSizingDialogProps> = ({
  open,
  onClose,
  person,
}) => {
  const { currentCustomer } = useIemFrontDoorStore();
  const isViewer = useWriteGuard();
  const { hasRole } = useAuth();
  const { myPersonEntityId, isLoading: isPersonIdLoading } = useMyPersonEntityId();
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const targetPerson = person || currentCustomer;

  // service-member may only edit their own sizing record; all other roles unrestricted
  const isServiceMember = hasRole('service-member');
  const isOwnRecord = !!targetPerson && targetPerson.id === myPersonEntityId;
  // Read-only when: global viewer mode OR service-member viewing another person's record
  // isPersonIdLoading guard prevents briefly marking own record as read-only while identity resolves
  const isSizingReadOnly = isViewer || (isServiceMember && !isPersonIdLoading && !isOwnRecord);
  const { label: ieKitLabel, labelPlural: ieKitLabelPlural } = useIeKitLabels('front_window');

  // State
  const [sizingItems, setSizingItems] = useState<Record<string, SizingDraft>>({});
  const [initialized, setInitialized] = useState(false);
  const [availableSearch, setAvailableSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isValidatingImport, setIsValidatingImport] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'paired' | 'unpaired'>('all');
  const [selectedKitId, setSelectedKitId] = useState<string>('all');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Track initial sizing state to detect unsaved changes
  const initialSizingItemsRef = useRef<Record<string, SizingDraft>>({});

  // Size Selection State
  const [sizeSelectOpen, setSizeSelectOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState<Entity | null>(null);

  // Delete Confirmation State
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<SelectionItem | null>(null);

  // --- Data Fetching ---

  // 1. Fetch All Catalog Items (Parents and Children)
  // We fetch all because we need children for the size selector and for resolving names in the right pane.
  // Assuming the catalog isn't massive (thousands). If it is, we might need more targeted fetching.
  const {
    data: allCatalogItems = [],
    isLoading: catalogLoading,
    error: catalogError,
  } = useAllEntities({
    type: 'catalog',
    filters: { status: 'eq.active' },
    enabled: open,
  });

  // 2. Fetch Existing Sizing for Person
  const {
    data: existingSizing = [],
    isLoading: sizingLoading,
    error: sizingError,
  } = useEntities({
    type: 'person_sizing',
    filters: targetPerson ? { 'entity_data->>person_id': `eq.${targetPerson.id}` } : undefined,
    limit: 1000,
    enabled: open && !!targetPerson,
  });

  // 3. Fetch Kits
  const { data: kits = [] } = useEntities({
    type: 'ie_kit',
    filters: { status: 'eq.active' },
    limit: 1000,
    enabled: open,
  });

  // 4. Fetch Kit Items (if kit selected)
  const { data: kitItems = [] } = useEntities({
    type: 'ie_kit_item',
    filters:
      selectedKitId !== 'all' ? { 'entity_data->>ie_kit_id': `eq.${selectedKitId}` } : undefined,
    limit: 2000,
    enabled: open && selectedKitId !== 'all',
  });

  // --- Computed Data ---

  const catalogLookup = useMemo(
    () => new Map(allCatalogItems.map((i: Entity) => [i.id, i])),
    [allCatalogItems],
  );

  const parentCatalogItems = useMemo(() => {
    const parentIds = buildParentIdSet(allCatalogItems);
    return allCatalogItems.filter((i: Entity) => parentIds.has(i.id));
  }, [allCatalogItems]);

  const childCatalogItems = useMemo(
    () => allCatalogItems.filter((i: Entity) => isCatalogVariant(i)),
    [allCatalogItems],
  );

  const childrenByParent = useMemo(() => {
    const map = new Map<string, Entity[]>();
    childCatalogItems.forEach((child: Entity) => {
      const pid = (child.entity_data as CatalogEntityData)?.parent_id;
      if (pid) {
        if (!map.has(pid)) map.set(pid, []);
        map.get(pid)?.push(child);
      }
    });
    return map;
  }, [childCatalogItems]);

  const sizeableParentItems = useMemo(
    () => parentCatalogItems.filter((p) => (childrenByParent.get(p.id)?.length || 0) > 0),
    [parentCatalogItems, childrenByParent],
  );

  const allowedCatalogIds = useMemo(() => {
    if (selectedKitId === 'all') return null;
    return new Set(
      kitItems.map((item: Entity) => (item.entity_data as SizingEntityData)?.catalog_id),
    );
  }, [selectedKitId, kitItems]);

  const totalCount = useMemo(() => {
    if (selectedKitId === 'all' || !allowedCatalogIds) {
      return sizeableParentItems.length;
    }
    // Filter sizeableParentItems to only those in the kit
    return sizeableParentItems.filter((p) => allowedCatalogIds.has(p.id)).length;
  }, [selectedKitId, allowedCatalogIds, sizeableParentItems]);

  const sizedCount = useMemo(() => {
    const allSizedParents = Object.values(sizingItems).map((i) => i.parentId);
    if (selectedKitId === 'all' || !allowedCatalogIds) {
      return allSizedParents.length;
    }
    return allSizedParents.filter((pid) => allowedCatalogIds.has(pid)).length;
  }, [sizingItems, selectedKitId, allowedCatalogIds]);

  // --- Initialization ---

  useEffect(() => {
    if (!open || initialized || sizingLoading || !targetPerson) return;

    const next: Record<string, SizingDraft> = {};
    existingSizing.forEach((entity: Entity) => {
      const catId = (entity.entity_data as SizingEntityData)?.catalog_id;
      if (catId) {
        // Try to get parentId from the entity itself first (after backfill)
        let parentId = (entity.entity_data as SizingEntityData)?.parent_id;

        // Fallback to lookup if not present
        if (!parentId) {
          const catalogItem = catalogLookup.get(catId);
          parentId = (catalogItem?.entity_data as CatalogEntityData)?.parent_id;
        }

        if (parentId) {
          next[catId] = { catalogId: catId, parentId };
        }
      }
    });
    initialSizingItemsRef.current = next;
    setSizingItems(next);
    setInitialized(true);
  }, [open, initialized, existingSizing, sizingLoading, targetPerson, catalogLookup]);

  // Reset on close/re-open
  useEffect(() => {
    if (!open) {
      setInitialized(false);
      setSizingItems({});
      initialSizingItemsRef.current = {};
      setErrorMessage(null);
      setIsImporting(false);
      setIsValidatingImport(false);
      setFilterMode('all');
      setSelectedKitId('all');
    }
  }, [open]);

  // Unsaved changes detection
  const isDirty = useMemo(() => {
    if (!initialized) return false;
    const currentKeys = Object.keys(sizingItems).sort();
    const initialKeys = Object.keys(initialSizingItemsRef.current).sort();
    if (currentKeys.length !== initialKeys.length) return true;
    return currentKeys.some((key, i) => key !== initialKeys[i]);
  }, [sizingItems, initialized]);

  // --- List Preparation ---

  // Helper to check if a parent is "paired" (has at least one child selected)
  const isParentPaired = useCallback(
    (parentId: string) => {
      return Object.values(sizingItems).some((item) => item.parentId === parentId);
    },
    [sizingItems],
  );

  const leftItems: SelectionItem[] = useMemo(() => {
    const needle = availableSearch.trim().toLowerCase();

    return parentCatalogItems
      .filter((parent: Entity) => {
        // Search Filter
        if (needle && !(parent.display_name || '').toLowerCase().includes(needle)) return false;

        // Status Filter
        const paired = isParentPaired(parent.id);
        if (filterMode === 'paired' && !paired) return false;
        if (filterMode === 'unpaired' && paired) return false;

        // Kit Filter
        if (allowedCatalogIds && !allowedCatalogIds.has(parent.id)) return false;

        return true;
      })
      .map((parent: Entity) => {
        const paired = isParentPaired(parent.id);
        const childCount = childrenByParent.get(parent.id)?.length || 0;
        const sizeText = childCount === 1 ? '1 size' : `${childCount} sizes`;

        return {
          id: parent.id,
          primaryText: parent.display_name || 'Unknown',
          secondaryText: `${paired ? 'Paired' : 'Unpaired'} • ${sizeText}`,
          data: parent,
          disabled: childCount === 0,
        };
      });
  }, [
    parentCatalogItems,
    availableSearch,
    filterMode,
    isParentPaired,
    childrenByParent,
    allowedCatalogIds,
  ]);

  const rightItems: SelectionItem[] = useMemo(() => {
    const needle = assignedSearch.trim().toLowerCase();

    return Object.values(sizingItems)
      .map((draft) => {
        const child = catalogLookup.get(draft.catalogId);
        const parent = catalogLookup.get(draft.parentId);
        return {
          id: draft.catalogId,
          primaryText: formatNomenclatureWithSize(
            child?.display_name || 'Unknown Item',
            (child?.entity_data as CatalogEntityData)?.size,
          ),
          secondaryText: parent?.display_name || 'Unknown Parent',
          data: draft,
        };
      })
      .filter((item) => !needle || item.primaryText.toLowerCase().includes(needle))
      .sort((a, b) => a.primaryText.localeCompare(b.primaryText));
  }, [sizingItems, assignedSearch, catalogLookup]);

  // --- Handlers ---

  const handleLeftItemAdd = (item: SelectionItem) => {
    const parent = item.data as Entity;
    setSelectedParent(parent);
    setSizeSelectOpen(true);
  };

  const handleSizeSelect = (child: Entity) => {
    const parentId = (child.entity_data as CatalogEntityData)?.parent_id;
    if (parentId) {
      setSizingItems((prev) => {
        const next = { ...prev };
        // Remove any existing selection for this parent to enforce one size per parent
        Object.keys(next).forEach((key) => {
          if (next[key]?.parentId === parentId) {
            delete next[key];
          }
        });
        next[child.id] = { catalogId: child.id, parentId };
        return next;
      });
    }
    setSizeSelectOpen(false);
    setSelectedParent(null);
  };

  const handleRightItemRemove = (item: SelectionItem) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (itemToDelete) {
      setSizingItems((prev) => {
        const next = { ...prev };
        delete next[itemToDelete.id];
        return next;
      });
    }
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
  };

  const handleImportFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !targetPerson) return;

    setErrorMessage(null);
    setIsImporting(true);
    setIsValidatingImport(false);

    try {
      const { importedDrafts, importedCount } = await parsePersonSizingImportFile(
        file,
        allCatalogItems,
      );
      const importedParentIds = new Set(
        Object.values(importedDrafts).map((draft) => draft.parentId),
      );

      const mergedSizingItems = { ...sizingItems };
      Object.keys(mergedSizingItems).forEach((catalogId) => {
        const existing = mergedSizingItems[catalogId];
        if (existing && importedParentIds.has(existing.parentId)) {
          delete mergedSizingItems[catalogId];
        }
      });
      Object.entries(importedDrafts).forEach(([catalogId, draft]) => {
        mergedSizingItems[catalogId] = {
          catalogId: draft.catalogId,
          parentId: draft.parentId,
        };
      });

      const assignments: PersonSizingAssignmentInput[] = Object.values(mergedSizingItems).map(
        (draft) => ({
          catalogId: draft.catalogId,
          parentId: draft.parentId,
        }),
      );

      setIsValidatingImport(true);
      await personSizingService.validatePersonSizingAssignments(targetPerson.id, assignments);

      setSizingItems(mergedSizingItems);

      notifications.success(
        'Import Successful',
        `Validated and loaded ${importedCount} sizing assignment${importedCount === 1 ? '' : 's'}. Review and click Save Changes to persist.`,
      );
    } catch (err: unknown) {
      const importErrorMessage = getErrorMessage(err);
      setErrorMessage(importErrorMessage);
      notifications.error('Import Failed', importErrorMessage);
    } finally {
      setIsImporting(false);
      setIsValidatingImport(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (!targetPerson) return;
    setIsSaving(true);
    setErrorMessage(null);

    try {
      const assignments: PersonSizingAssignmentInput[] = Object.values(sizingItems).map(
        (draft) => ({
          catalogId: draft.catalogId,
          parentId: draft.parentId,
        }),
      );

      await personSizingService.savePersonSizingAssignments(targetPerson.id, assignments);

      await queryClient.invalidateQueries({ queryKey: ['entities'] });
      await queryClient.invalidateQueries({ queryKey: ['sizing'] });
      await queryClient.invalidateQueries({ queryKey: ['ie-summary'] });
      notifications.success('Sizing Saved', 'Person sizing updated successfully.');
      onClose();
    } catch (err: unknown) {
      log.error('Failed to save sizing', { error: String(err) });
      const errorMessage = getErrorMessage(err);
      setErrorMessage(errorMessage);
      notifications.error('Error', errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // --- Render ---

  const headerContent = (
    <Stack direction="row" spacing={2} alignItems="center">
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Filter by {ieKitLabel}</InputLabel>
        <Select
          value={selectedKitId}
          label={`Filter by ${ieKitLabel}`}
          onChange={(e) => setSelectedKitId(e.target.value)}
        >
          <MenuItem value="all">All {ieKitLabelPlural}</MenuItem>
          {kits.map((kit) => (
            <MenuItem key={kit.id} value={kit.id}>
              {kit.display_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>Filter Catalog Items</InputLabel>
        <Select
          value={filterMode}
          label="Filter Catalog Items"
          onChange={(e) => setFilterMode(e.target.value as FilterModeValue)}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="paired">Only Paired</MenuItem>
          <MenuItem value="unpaired">Only Unpaired</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  const extraActions = (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />
      <Button
        variant="outlined"
        onClick={() => fileInputRef.current?.click()}
        disabled={
          isSizingReadOnly ||
          isImporting ||
          isValidatingImport ||
          isSaving ||
          catalogLoading ||
          allCatalogItems.length === 0
        }
      >
        {isValidatingImport ? 'Validating...' : isImporting ? 'Importing...' : 'Import'}
      </Button>
    </>
  );

  if (!targetPerson) {
    return null;
  }

  return (
    <>
      {isServiceMember && !isOwnRecord && targetPerson && (
        <Alert severity="info" sx={{ mb: 0 }}>
          Viewing {targetPerson.display_name ?? 'this person'}'s sizing — editing is restricted to
          your own record.
        </Alert>
      )}
      <DualPaneSelectionDialog
        open={open}
        title={`Person Sizing: ${targetPerson.display_name}`}
        onClose={onClose}
        isDirty={isDirty}
        onSave={handleSave}
        isSaving={isSaving}
        saveDisabled={isSizingReadOnly}
        saveLabel="Save Changes"
        errorMessage={errorMessage}
        onClearError={() => setErrorMessage(null)}
        headerContent={headerContent}
        // Left Pane
        leftTitle="Catalog Items"
        leftItems={leftItems}
        leftSearch={availableSearch}
        onLeftSearchChange={setAvailableSearch}
        onLeftItemAdd={handleLeftItemAdd}
        leftLoading={catalogLoading}
        leftError={!!catalogError}
        // Right Pane
        rightTitle="Assigned Sizes"
        rightItems={rightItems}
        rightSearch={assignedSearch}
        onRightSearchChange={setAssignedSearch}
        onRightItemRemove={handleRightItemRemove}
        rightLoading={sizingLoading}
        rightError={!!sizingError}
        extraActions={extraActions}
        readOnly={isSizingReadOnly}
        footerInfo={
          <Typography variant="body2" sx={{ mr: 2 }}>
            {sizedCount} / {totalCount} Items Sized
          </Typography>
        }
      />

      <SizeSelectionDialog
        open={sizeSelectOpen}
        parentItem={selectedParent}
        childItems={selectedParent ? childrenByParent.get(selectedParent.id) || [] : []}
        onSelect={handleSizeSelect}
        onClose={() => {
          setSizeSelectOpen(false);
          setSelectedParent(null);
        }}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Assigned Size</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove <strong>{itemToDelete?.primaryText}</strong> from the
            assigned sizes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
